<template>
  <div class="fuel-analytics bg-white rounded-lg shadow-sm p-6">
    <h3 class="text-lg font-semibold mb-4">Fuel Analytics</h3>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-8">
      <LoadingSpinner class="mx-auto" />
      <p class="text-gray-600 mt-2">Loading analytics...</p>
    </div>

    <!-- Analytics Grid (derived from store) -->
    <div
      v-else-if="transactions && transactions.length"
      class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6"
    >
      <AnalyticsCard
        title="Total Fuel"
        :value="`${totalFuelUsed.toFixed(1)}L`"
        icon="⛽"
        trend="up"
        :trend-value="totalFuelUsed"
      />
      <AnalyticsCard
        title="Total Cost"
        :value="`tsh${totalFuelCost.toFixed(2)}`"
        icon="💰"
        trend="up"
        :trend-value="totalFuelCost"
      />
      <AnalyticsCard
        title="Avg Cost/L"
        :value="`tsh${averageCostPerLiter.toFixed(2)}`"
        icon="📊"
        trend="neutral"
        :trend-value="averageCostPerLiter"
      />
      <AnalyticsCard
        title="Transactions"
        :value="transactions.length.toString()"
        icon="📝"
        trend="up"
        :trend-value="transactions.length"
      />
    </div>

    <!-- Date Range Selector -->
    <div class="bg-gray-50 rounded-lg p-4 mb-6">
      <h4 class="text-sm font-medium mb-3">Filter by Date Range</h4>
      <div class="flex flex-col sm:flex-row gap-3">
        <select v-model="timeRange" class="flex-1 p-2 border border-gray-300 rounded-md">
          <option value="7">Last 7 Days</option>
          <option value="30">Last 30 Days</option>
          <option value="90">Last 90 Days</option>
          <option value="365">Last Year</option>
          <option value="custom">Custom Range</option>
        </select>

        <div v-if="timeRange === 'custom'" class="flex flex-col sm:flex-row gap-2 flex-1">
          <input
            type="date"
            v-model="customStartDate"
            class="p-2 border border-gray-300 rounded-md"
          />
          <span class="hidden sm:inline self-center">to</span>
          <input
            type="date"
            v-model="customEndDate"
            class="p-2 border border-gray-300 rounded-md"
          />
        </div>

        <button @click="updateAnalytics" class="btn-primary whitespace-nowrap">Apply Filter</button>
      </div>
    </div>

    <!-- Equipment Selector -->
    <div class="bg-gray-50 rounded-lg p-4 mb-6">
      <h4 class="text-sm font-medium mb-3">Filter by Equipment</h4>
      <div class="flex flex-col sm:flex-row gap-3">
        <select v-model="selectedEquipment" class="flex-1 p-2 border border-gray-300 rounded-md">
          <option :value="null">All Equipment</option>
          <option v-for="eq in fuelEquipment" :key="eq.id" :value="eq.id">
            {{ eq.name }} ({{ eq.type }})
          </option>
        </select>
        <button @click="updateAnalytics" class="btn-primary whitespace-nowrap">Apply Filter</button>
      </div>
    </div>

    <!-- Charts Placeholder -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div class="bg-gray-50 rounded-lg p-4 text-center">
        <h4 class="text-sm font-medium mb-2">Fuel Consumption Trend</h4>
        <p class="text-gray-500 text-sm">Chart would show here</p>
      </div>
      <div class="bg-gray-50 rounded-lg p-4 text-center">
        <h4 class="text-sm font-medium mb-2">Cost Analysis</h4>
        <p class="text-gray-500 text-sm">Chart would show here</p>
      </div>
    </div>

    <!-- Equipment Efficiency Table -->
    <div class="mt-6">
      <h4 class="text-sm font-medium mb-3">Equipment Fuel Efficiency</h4>
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Equipment
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Fuel Used (L)
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Total Cost
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Avg Cost/L
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Efficiency
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="stat in equipmentStats" :key="stat.equipment_id">
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {{ getEquipmentName(stat.equipment_id) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ stat.total_fuel_used.toFixed(1) }}L
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                tsh{{ (stat.total_fuel_used * stat.average_cost_per_liter).toFixed(2) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                tsh{{ stat.average_cost_per_liter.toFixed(2) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">N/A</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useFuelStore } from '@/stores/fuelStore'
import { useEquipmentStore } from '@/stores/equipmentStore'
import AnalyticsCard from '@/components/shared/AnalyticsCard.vue'
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue'
import type { Ref } from 'vue'
import type { FuelEquipmentStats, FuelTransaction } from '@/types/fuel'
import type { Equipment } from '@/types/equipment'

const fuelStore = useFuelStore()
const equipmentStore = useEquipmentStore()

const timeRange = ref<'7' | '30' | '90' | '365' | 'custom'>('30')
const selectedEquipment = ref<number | null>(null)
const customStartDate = ref('')
const customEndDate = ref('')

// use store refs provided by fuelStore with explicit typing
const refs = storeToRefs(fuelStore) as unknown as {
  equipmentStats: Ref<FuelEquipmentStats[]>
  loading: Ref<boolean>
  totalFuelUsed: Ref<number>
  totalFuelCost: Ref<number>
  averageCostPerLiter: Ref<number>
  transactions: Ref<FuelTransaction[]>
}

const { equipmentStats, loading, totalFuelUsed, totalFuelCost, averageCostPerLiter, transactions } =
  refs

const fuelEquipment = computed(() =>
  Array.isArray(equipmentStore.equipment)
    ? (equipmentStore.equipment as Equipment[]).filter((eq) => eq.requires_fuel)
    : [],
)

onMounted(() => {
  // fetch transactions and compute stats
  fuelStore.fetchTransactions()
  fuelStore.fetchEquipmentStats()
  equipmentStore.fetchEquipment()
})

const updateAnalytics = () => {
  // simply refetch transactions and recompute stats for now
  fuelStore.fetchTransactions()
  fuelStore.fetchEquipmentStats()
}

const getEquipmentName = (id: number) => {
  const list = Array.isArray(equipmentStore.equipment)
    ? (equipmentStore.equipment as Equipment[])
    : []
  const eq = list.find((e) => e.id === Number(id))
  return eq ? eq.name : 'Unknown'
}
</script>
